import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { LANGUAGES, useI18n } from './i18n';
import { currentLocale } from './langState';
import { FoundingCard } from './Founding';
import { needsHomeScreen, pushStatus, turnOffPush, turnOnPush } from './pushClient';
import { TwoStepSection } from './TwoFactor';
import TopstepSync from './TopstepSync';
import ShareLinks from './ShareLinks';
import { featureOpen } from './AppNav';

function ProfileSection({ user, onUserChange, onSessionExpired }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: user.name, email: user.email, currentPassword: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const emailChanged = form.email.trim().toLowerCase() !== user.email;
  const dirty = form.name.trim() !== user.name || emailChanged;

  function set(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
    setNotice('');
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const updated = await api('/auth/profile', {
        method: 'PATCH',
        body: { name: form.name, email: form.email, currentPassword: emailChanged ? form.currentPassword : undefined },
      });
      onUserChange(updated);
      setForm({ name: updated.name, email: updated.email, currentPassword: '' });
      setNotice(emailChanged ? t('Saved. Log in with {email} from now on.', { email: updated.email }) : t('Saved.'));
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="settings-card" onSubmit={save}>
      <h2>{t('Profile')}</h2>
      <div className="settings-fields">
        <label>
          {t('Name')}
          <input name="name" value={form.name} onChange={set} maxLength={80} autoComplete="name" required />
        </label>
        <label>
          {t('Email')}
          <input name="email" type="email" value={form.email} onChange={set} autoComplete="email" required />
        </label>
        {emailChanged && !user.hasPassword && (
          <p className="field-hint">{t('To change your email, first set a password in the Password section below.')}</p>
        )}
        {emailChanged && user.hasPassword && (
          <label>
            {t('Current password')}
            <input name="currentPassword" type="password" value={form.currentPassword} onChange={set}
              autoComplete="current-password" required />
            <span className="field-hint">{t("Needed to change your email. We'll let your old address know about the change.")}</span>
          </label>
        )}
      </div>
      {user.google && <p className="settings-text google-linked">{t('Linked to Google: you can log in with Continue with Google.')}</p>}
      {error && <p className="error">{error}</p>}
      {notice && <p className="success">{notice}</p>}
      <div className="settings-actions">
        <button type="submit" className="small" disabled={!dirty || saving || (emailChanged && (!user.hasPassword || !form.currentPassword))}>
          {saving ? t('Saving…') : t('Save changes')}
        </button>
      </div>
    </form>
  );
}

function PasswordSection({ user, onUserChange, onSessionExpired }) {
  const { t } = useI18n();
  const empty = { currentPassword: '', newPassword: '', confirm: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const mismatch = form.confirm && form.newPassword !== form.confirm;
  const tooShort = form.newPassword && form.newPassword.length < 8;
  // An account made with Google has no password yet: it sets one, without a current password.
  const first = !user.hasPassword;

  function set(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
    setNotice('');
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const updated = await api('/auth/password', {
        method: 'POST',
        body: { currentPassword: form.currentPassword, newPassword: form.newPassword },
      });
      onUserChange(updated);
      setForm(empty);
      setNotice(first ? t('Password set. You can now also log in with your email and password.') : t('Password changed. Any other devices you were logged in on have been signed out.'));
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="settings-card" onSubmit={save}>
      <h2>{t('Password')}</h2>
      {first && <p className="settings-text">{t('You log in with Google. Set a password if you’d also like to log in with your email.')}</p>}
      <div className="settings-fields">
        {!first && (
          <label>
            {t('Current password')}
            <input name="currentPassword" type="password" value={form.currentPassword} onChange={set}
              autoComplete="current-password" required />
          </label>
        )}
        <label>
          {t('New password')}
          <input name="newPassword" type="password" value={form.newPassword} onChange={set} autoComplete="new-password"
            minLength={8} required />
          <span className={`field-hint${tooShort ? ' error-text' : ''}`}>{t('At least 8 characters.')}</span>
        </label>
        <label>
          {t('Confirm new password')}
          <input name="confirm" type="password" value={form.confirm} onChange={set} autoComplete="new-password" required />
          {mismatch && <span className="field-hint error-text">{t("The passwords don't match.")}</span>}
        </label>
      </div>
      {error && <p className="error">{error}</p>}
      {notice && <p className="success">{notice}</p>}
      <div className="settings-actions">
        <button type="submit" className="small"
          disabled={saving || (!first && !form.currentPassword) || form.newPassword.length < 8 || form.newPassword !== form.confirm}>
          {saving ? t('Saving…') : first ? t('Set password') : t('Change password')}
        </button>
      </div>
    </form>
  );
}

const REMINDER_TIMES = [];
for (let m = 6 * 60; m <= 10 * 60; m += 30) REMINDER_TIMES.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
// The End-of-day reminder: after the close, 2 PM to 8 PM.
const EVENING_TIMES = [];
for (let m = 14 * 60; m <= 20 * 60; m += 30) EVENING_TIMES.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);

function timeLabel(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });
}

// A readable name for a time zone, e.g. "Central Standard Time".
function zoneName(zone) {
  try {
    const part = new Intl.DateTimeFormat(currentLocale(), { timeZone: zone, timeZoneName: 'long' }).formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return part ? part.value : zone;
  } catch {
    return zone.replace(/_/g, ' ');
  }
}

function EmailSection({ user, onUserChange, onSessionExpired }) {
  const { t } = useI18n();
  const prefs = { weeklyRecap: true, morningReminder: false, reminderTime: '08:00', eveningReminder: false, eveningTime: '16:00', ...(user.emailPrefs || {}) };
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [sending, setSending] = useState(false);
  const zone = user.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  async function save(changes) {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      onUserChange(await api('/email/preferences', { method: 'PATCH', body: changes }));
      setNotice(t('Saved.'));
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function sendSample() {
    setSending(true);
    setError('');
    setNotice('');
    try {
      await api('/email/test-recap', { method: 'POST' });
      setNotice(t('Sent to {email}. It can take a minute to arrive; check your spam folder too.', { email: user.email }));
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="settings-card">
      <h2>{t('Emails')}</h2>
      <label className="settings-switch">
        <input type="checkbox" checked={prefs.weeklyRecap} disabled={saving} onChange={(e) => save({ weeklyRecap: e.target.checked })} />
        <span>
          <strong>{t('Weekly recap')}</strong>
          <span>{t('Every Sunday evening: your P&L, win rate, best setup, costliest mistake and your focus for next week.')}</span>
        </span>
      </label>
      <label className="settings-switch">
        <input type="checkbox" checked={prefs.morningReminder} disabled={saving} onChange={(e) => save({ morningReminder: e.target.checked })} />
        <span>
          <strong>{t('Morning prep reminder')}</strong>
          <span>{t('A weekday nudge to check in, read your rules and write your plan before the open.')}</span>
        </span>
      </label>
      {prefs.morningReminder && (
        <label className="settings-inline">
          {t('Send it at')}
          <select value={prefs.reminderTime} disabled={saving} onChange={(e) => save({ reminderTime: e.target.value })}>
            {REMINDER_TIMES.map((x) => <option key={x} value={x}>{timeLabel(x)}</option>)}
          </select>
          <span className="field-hint">{t('your time ({zone}), Monday to Friday', { zone: zoneName(zone) })}</span>
        </label>
      )}
      <label className="settings-switch">
        <input type="checkbox" checked={prefs.eveningReminder} disabled={saving} onChange={(e) => save({ eveningReminder: e.target.checked })} />
        <span>
          <strong>{t('End-of-day reflection reminder')}</strong>
          <span>{t('A nudge after the close to review the day while it’s fresh. It only comes on days you traded and haven’t reflected yet, by email and on your phone if notifications are on.')}</span>
        </span>
      </label>
      {prefs.eveningReminder && (
        <label className="settings-inline">
          {t('Send it at')}
          <select value={prefs.eveningTime} disabled={saving} onChange={(e) => save({ eveningTime: e.target.value })}>
            {EVENING_TIMES.map((x) => <option key={x} value={x}>{timeLabel(x)}</option>)}
          </select>
          <span className="field-hint">{t('your time ({zone}), Monday to Friday', { zone: zoneName(zone) })}</span>
        </label>
      )}
      {error && <p className="error">{error}</p>}
      {notice && <p className="success">{notice}</p>}
      <div className="settings-actions">
        <button type="button" className="secondary small" onClick={sendSample} disabled={sending}>
          {sending ? t('Sending…') : t('Email me a sample recap now')}
        </button>
      </div>
    </section>
  );
}

// Push notifications: on or off for this device, and which kinds to get.
function PushSection({ user, onUserChange, onSessionExpired }) {
  const { t } = useI18n();
  const prefs = user.pushPrefs || { reminders: true, market: true, admin: true, limits: true };
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const refresh = () => pushStatus().then(setStatus).catch(() => setStatus({ supported: false }));
  useEffect(() => { refresh(); }, []);
  // Links like "Turn on push notifications" land here.
  useEffect(() => {
    if (status && window.location.hash === '#notifications') document.getElementById('notifications')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [status]);

  async function run(fn) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setBusy(false);
      refresh();
    }
  }
  const turnOn = () => run(async () => {
    const result = await turnOnPush();
    if (result === 'denied') setError(t('Your browser blocked notifications. Allow them for marketmatrixhq.com in your browser settings, then try again.'));
    else if (result === 'unsupported') setError(t('This browser doesn’t support push notifications.'));
    else setNotice(t('Push notifications are on for this device.'));
  });
  const turnOff = () => run(async () => { await turnOffPush(); setNotice(t('Push notifications are off for this device.')); });
  const test = () => run(async () => {
    const r = await api('/push/test', { method: 'POST' });
    setNotice(r.local ? t('Test notifications only send from the live site.') : r.sent ? t('Sent. It should pop up in a few seconds.') : t('No devices are turned on yet.'));
  });
  const setPref = (k) => (e) => run(async () => {
    const next = await api('/push/prefs', { method: 'PUT', body: { [k]: e.target.checked } });
    onUserChange({ ...user, pushPrefs: next });
  });

  const kinds = [
    ['reminders', t('Your reminders'), t('The reminders you set on your calendar, at the alert time you chose.')],
    ['market', t('Market events'), t('Economic data and earnings you set an alert for on the Market events page.')],
    ['limits', t('Stop for the day'), t('When synced trades use 75% of an account’s daily loss limit, and when they reach it.')],
    ...(user.isAdmin ? [['admin', t('Admin alerts'), t('New messages and chats, new members and problems on the site.')]] : []),
  ];
  return (
    <section className="settings-card" id="notifications">
      <h2>{t('Push notifications')}</h2>
      <p className="settings-text">{t('Alerts on your computer or phone, even when Market Matrix isn’t open, as long as your browser is running. Turn them on separately on each device.')}</p>
      {!status ? <p className="settings-text">{t('Loading…')}</p> : !status.supported ? (
        <p className="settings-text">{needsHomeScreen()
          ? t('On iPhone and iPad, push notifications work after you add Market Matrix to your home screen: tap Share, then Add to Home Screen, and open it from there.')
          : t('This browser doesn’t support push notifications. Try Chrome, Edge, Firefox or Safari.')}</p>
      ) : (
        <>
          <div className="push-device">
            <span className={`push-dot${status.thisDevice ? ' on' : ''}`} aria-hidden="true" />
            <strong>{status.thisDevice ? t('On for this device') : t('Off for this device')}</strong>
            {status.devices > 0 && <span className="field-hint">{t(status.devices === 1 ? '{n} device on' : '{n} devices on', { n: status.devices })}</span>}
          </div>
          {kinds.map(([k, label, hint]) => (
            <label key={k} className="settings-switch">
              <input type="checkbox" checked={prefs[k] !== false} disabled={busy} onChange={setPref(k)} />
              <span><strong>{label}</strong><span>{hint}</span></span>
            </label>
          ))}
          <div className="settings-actions">
            {status.thisDevice
              ? <button type="button" className="secondary small" onClick={turnOff} disabled={busy}>{t('Turn off on this device')}</button>
              : <button type="button" className="small" onClick={turnOn} disabled={busy}>{t('Turn on for this device')}</button>}
            {status.devices > 0 && <button type="button" className="secondary small" onClick={test} disabled={busy}>{t('Send a test')}</button>}
          </div>
        </>
      )}
      {error && <p className="error">{error}</p>}
      {notice && <p className="success">{notice}</p>}
    </section>
  );
}

// "Connect your AI" (connector.js): Market Matrix as a connector in the trader's own Claude or
// ChatGPT. The address to add, the steps for each, example questions, and the connected apps.
function ConnectAiSection({ onSessionExpired }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const load = () => api('/connector/apps').then(setData).catch((err) => (err.status === 401 ? onSessionExpired() : setData({ available: false })));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (data && window.location.hash === '#connect-ai') document.getElementById('connect-ai')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [data]);
  if (!data || !data.available) return null;

  async function copy() {
    try { await navigator.clipboard.writeText(data.url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { setCopied(false); }
  }
  async function disconnect(app) {
    if (!window.confirm(t('Disconnect {name}? It won’t be able to read your journal anymore.', { name: app.name }))) return;
    try { await api(`/connector/apps/${app.id}`, { method: 'DELETE' }); load(); } catch (err) { setError(err.message); }
  }
  const date = (d) => new Date(d).toLocaleDateString(currentLocale(), { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <section className={`settings-card${data.preview ? ' admin-preview-box' : ''}`} id="connect-ai">
      <h2>{t('Connect your AI')} {data.preview && <span className="admin-preview-badge">{t('Admin only')}</span>}</h2>
      <p className="settings-text">{t('Use your own Claude or ChatGPT to review your trading: add Market Matrix as a connector and ask it anything about your trades, accounts, journal and rules. It uses your own AI plan, and it’s read-only: it can’t change anything.')}</p>
      <div className="connect-url">
        <code>{data.url}</code>
        <button type="button" className="secondary small" onClick={copy}>{copied ? t('Copied ✓') : t('Copy')}</button>
      </div>
      <div className="connect-steps">
        <div>
          <h3>{t('In Claude')}</h3>
          <ol>
            <li>{t('On claude.ai (or the Claude app), open Customize, then Connectors.')}</li>
            <li>{t('Click Add custom connector, name it Market Matrix and paste the address above.')}</li>
            <li>{t('Click Add, then Connect, and allow access on the Market Matrix page that opens.')}</li>
            <li>{t('In a chat, turn on Market Matrix under the + button, then ask away.')}</li>
          </ol>
          <p className="field-hint">{t('Works on every Claude plan, including Free (one custom connector).')}</p>
        </div>
        <div>
          <h3>{t('In ChatGPT')}</h3>
          <ol>
            <li>{t('On chatgpt.com, turn on Developer mode in Settings, then Apps.')}</li>
            <li>{t('Add a custom MCP server (or app): name it Market Matrix, paste the address above and choose OAuth.')}</li>
            <li>{t('Sign in and allow access on the Market Matrix page that opens.')}</li>
            <li>{t('In a chat, pick Market Matrix from the tools menu, then ask away.')}</li>
          </ol>
          <p className="field-hint">{t('ChatGPT allows custom connectors on Pro, Business, Enterprise and Edu (not Free, Go or Plus), and the menus change often.')}</p>
        </div>
      </div>
      <h3>{t('Try asking')}</h3>
      <ul className="connect-examples">
        <li>“{t('Review my trading this week: what did I do well, and what were my biggest mistakes?')}”</li>
        <li>“{t('Which setup in my Playbook actually makes money, and which should I drop?')}”</li>
        <li>“{t('Am I breaking my own rules? Show me the trades.')}”</li>
        <li>“{t('How close am I to my prop firm’s max loss, and what should my risk per trade be?')}”</li>
      </ul>
      <h3>{t('Connected apps')}</h3>
      {data.apps.length ? (
        <ul className="connect-apps">
          {data.apps.map((a) => (
            <li key={a.id}>
              <span><strong>{a.name}</strong><span className="field-hint">{t('Connected {date}', { date: date(a.connectedAt) })}{a.lastUsedAt ? ` · ${t('last used {date}', { date: date(a.lastUsedAt) })}` : ''}</span></span>
              <button type="button" className="secondary small" onClick={() => disconnect(a)}>{t('Disconnect')}</button>
            </li>
          ))}
        </ul>
      ) : <p className="settings-text">{t('No AI connected yet.')}</p>}
      {error && <p className="error">{error}</p>}
    </section>
  );
}

// Results in R: the usual dollars at risk per trade, for trades without their own stop or risk.
function RiskSection({ user, onUserChange, onSessionExpired }) {
  const { t } = useI18n();
  const [value, setValue] = useState(user.riskPerTrade ? String(user.riskPerTrade) : '');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  async function save(e) {
    e.preventDefault();
    setError(''); setNotice('');
    try {
      const r = await api('/auth/risk-per-trade', { method: 'PUT', body: { riskPerTrade: value === '' ? null : Number(value) } });
      onUserChange({ ...user, riskPerTrade: r.riskPerTrade });
      setNotice(r.riskPerTrade ? t('Saved. Trades without their own stop count R from {amount}.', { amount: `$${r.riskPerTrade}` }) : t('Cleared. Only trades with a stop or risk show R.'));
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    }
    return undefined;
  }
  return (
    <section className="settings-card" id="risk">
      <h2>{t('Results in R')}</h2>
      <p className="settings-text">{t('R is a result measured against what you risked: +2R made twice your risk, −1R lost it. Add the first stop to a trade in its window, or set your usual risk here for trades without one. Then pick R in the result display (Dollars, Points, R…) on the Trades page or Dashboard.')}</p>
      <form className="fields risk-setting" onSubmit={save}>
        <label>{t('Your usual risk per trade ($)')}<input type="number" min="0" step="any" value={value} onChange={(e) => setValue(e.target.value)} placeholder={t('e.g. 100')} /></label>
        <button type="submit" className="small">{t('Save')}</button>
      </form>
      {error && <p className="error">{error}</p>}
      {notice && <p className="success">{notice}</p>}
    </section>
  );
}

function DataSection({ onSessionExpired }) {
  const { t, tr } = useI18n();
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  // Fetches the file with the login cookie, then hands it to the browser as a download.
  async function download(path, fallbackName) {
    setBusy(path);
    setError('');
    try {
      const res = await fetch(`/api${path}`);
      if (res.status === 401) return onSessionExpired();
      if (!res.ok) throw new Error(t('Could not prepare your download. Please try again.'));
      const name = (res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = name ? name[1] : fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.message || t('Could not prepare your download. Please try again.'));
    } finally {
      setBusy('');
    }
  }

  return (
    <section className="settings-card">
      <h2>{t('Your data')}</h2>
      <p className="settings-text">
        {tr("Download a copy of everything you've put into Market Matrix. The trades file opens in Excel or Google Sheets and can be imported back into Market Matrix. See our <0>Privacy Policy</0> for how your data is handled.",
          [(x) => <Link to="/privacy">{x}</Link>])}
      </p>
      {error && <p className="error">{error}</p>}
      <div className="settings-actions settings-downloads">
        <button type="button" className="small" disabled={Boolean(busy)} onClick={() => download('/export/trades.csv', 'market-matrix-trades.csv')}>
          {busy === '/export/trades.csv' ? t('Preparing…') : t('Download trades (CSV)')}
        </button>
        <button type="button" className="secondary small" disabled={Boolean(busy)} onClick={() => download('/export/all.json', 'market-matrix-data.json')}>
          {busy === '/export/all.json' ? t('Preparing…') : t('Download all my data')}
        </button>
      </div>
    </section>
  );
}

// The server accepts the phrase in English or Spanish.
const DELETE_PHRASE = 'delete my account';

function DeleteSection({ user, onDeleted, onSessionExpired }) {
  const { t, tr } = useI18n();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const phrase = t(DELETE_PHRASE);
  const ready = (password || !user.hasPassword) && typed.trim().toLowerCase() === phrase;

  async function remove(e) {
    e.preventDefault();
    setDeleting(true);
    setError('');
    try {
      await api('/auth/account', { method: 'DELETE', body: { password, confirm: typed } });
      onDeleted();
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      setDeleting(false);
    }
  }

  return (
    <section className="settings-card settings-danger">
      <h2>{t('Delete account')}</h2>
      <p className="settings-text">
        {t("Permanently deletes your Market Matrix account and everything in it: your trades, accounts, journal notes, routine, goals, notebook and messages. This can't be undone.")}
      </p>
      {!open ? (
        <div className="settings-actions">
          <button type="button" className="secondary small" onClick={() => setOpen(true)}>{t('Delete my account…')}</button>
        </div>
      ) : (
        <form className="danger-confirm" onSubmit={remove}>
          <strong className="danger-confirm-title">{t('Are you sure?')}</strong>
          {user.hasPassword && (
            <label>
              <span>{t('Your password')}</span>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </label>
          )}
          <label>
            <span>{tr('Type <0>{phrase}</0> to confirm', [(x) => <strong>{x}</strong>], { phrase })}</span>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
          </label>
          {error && <p className="error">{error}</p>}
          <div className="account-row-actions">
            <button type="submit" className="danger small" disabled={!ready || deleting}>
              {deleting ? t('Deleting…') : t('Delete my account forever')}
            </button>
            <button type="button" className="link-button" onClick={() => { setOpen(false); setError(''); }} disabled={deleting}>
              {t('Cancel')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

// The site's language, saved to the account so emails match.
function LanguageSection() {
  const { t, lang, setLang } = useI18n();
  return (
    <section className="settings-card">
      <h2>{t('Language')}</h2>
      <p className="settings-text">{t('The language for Market Matrix and the emails we send you.')}</p>
      <div className="impact-buttons tracking-toggle" role="radiogroup" aria-label={t('Language')}>
        {LANGUAGES.map((l) => (
          <button key={l.id} type="button" role="radio" aria-checked={lang === l.id}
            className={`impact-button${lang === l.id ? ' selected tracking-on' : ''}`} onClick={() => setLang(l.id)}>
            {l.label}
          </button>
        ))}
      </div>
    </section>
  );
}

function Settings({ user, accounts, onUserChange, onDeleted, onSynced, onSessionExpired }) {
  const { t } = useI18n();
  return (
    <div className="settings-page">
      <div className="routine-header">
        <div>
          <p className="eyebrow">{t('Settings')}</p>
          <h1 className="routine-title">{t('Your account')}</h1>
          <p className="subtitle">{t('Update your details, change your password, download your data or delete your account.')}</p>
        </div>
      </div>
      <FoundingCard user={user} onUserChange={onUserChange} />
      <ProfileSection user={user} onUserChange={onUserChange} onSessionExpired={onSessionExpired} />
      <LanguageSection />
      <PasswordSection user={user} onUserChange={onUserChange} onSessionExpired={onSessionExpired} />
      <EmailSection user={user} onUserChange={onUserChange} onSessionExpired={onSessionExpired} />
      {!user.isDemo && <PushSection user={user} onUserChange={onUserChange} onSessionExpired={onSessionExpired} />}
      {!user.isDemo && <RiskSection user={user} onUserChange={onUserChange} onSessionExpired={onSessionExpired} />}
      {!user.isDemo && <ConnectAiSection onSessionExpired={onSessionExpired} />}
      {user.isAdmin && <TwoStepSection onSessionExpired={onSessionExpired} />}
      <TopstepSync user={user} accounts={accounts} onSynced={onSynced} onSessionExpired={onSessionExpired} />
      {(user.isAdmin || featureOpen(user, 'share')) && !user.isDemo && <ShareLinks accounts={accounts} preview={!featureOpen(user, 'share')} onSessionExpired={onSessionExpired} />}
      <DataSection onSessionExpired={onSessionExpired} />
      <DeleteSection user={user} onDeleted={onDeleted} onSessionExpired={onSessionExpired} />
    </div>
  );
}

export default Settings;
